/**
 * Bildmessgeräte (S15).
 *
 * Sascha: „Histogramm, RGB-Parade, Vektorskop — damit man sieht, ob ein Bild
 * sendefähig ist, statt es zu schätzen.“
 *
 * Hier steht das Messen, nicht das Zeichnen: drei Funktionen über die rohen
 * Bildpunkte, ohne Leinwand, ohne Browser. Damit lässt sich prüfen, *was*
 * gemessen wird -- eine Kurve, die schön aussieht und falsch rechnet, fällt
 * sonst niemandem auf.
 *
 * Gemessen wird immer an der Vorschau, also an dem Bild, das gerade dasteht.
 * Der Export rechnet in voller Auflösung; für die Frage „liegt hier etwas
 * ausserhalb“ ist das derselbe Befund, denn Skalieren verschiebt Farben nicht.
 */
/** Rec. 709 -- dieselben Gewichte wie im Shader und wie in ffmpeg. */
export const LUMA = [0.2126, 0.7152, 0.0722];
/**
 * Die Grenzen des Sendebereichs, in Stufen von 0..255. Alles darunter ist
 * „unter Schwarz“, alles darüber „über Weiss“ -- beides überlebt eine
 * Ausstrahlung nicht und ist im Bild später einfach weg.
 */
export const SENDE_UNTEN = 16;
export const SENDE_OBEN = 235;
function leer(n) {
    return new Array(n).fill(0);
}
/** Aus 0..255 das Fach, in das ein Wert gehört. */
function fach(wert, stufen) {
    const i = Math.floor((wert / 256) * stufen);
    return i < 0 ? 0 : i >= stufen ? stufen - 1 : i;
}
/**
 * Wie oft welcher Wert vorkommt -- je Kanal und für die Helligkeit.
 *
 * `daten` ist RGBA, so wie es `getImageData` liefert. Durchsichtige Punkte
 * zählen mit: eine Vorschau hat keine, und eine Ebene mit Loch soll das Loch
 * nicht verstecken.
 */
export function histogramm(daten, stufen = 64) {
    const r = leer(stufen);
    const g = leer(stufen);
    const b = leer(stufen);
    const luma = leer(stufen);
    let punkte = 0;
    let unten = 0;
    let oben = 0;
    for (let i = 0; i + 3 < daten.length; i += 4) {
        const cr = daten[i] ?? 0;
        const cg = daten[i + 1] ?? 0;
        const cb = daten[i + 2] ?? 0;
        r[fach(cr, stufen)] += 1;
        g[fach(cg, stufen)] += 1;
        b[fach(cb, stufen)] += 1;
        const y = LUMA[0] * cr + LUMA[1] * cg + LUMA[2] * cb;
        luma[fach(y, stufen)] += 1;
        if (y < SENDE_UNTEN)
            unten += 1;
        else if (y > SENDE_OBEN)
            oben += 1;
        punkte += 1;
    }
    return { stufen, r, g, b, luma, punkte, unten: punkte ? unten / punkte : 0, oben: punkte ? oben / punkte : 0 };
}
/**
 * Die RGB-Parade: für jede Spalte des Bildes, welche Werte darin vorkommen.
 *
 * Das ist der Blick, mit dem man sieht, ob eine Seite des Bildes absäuft,
 * während die andere in Ordnung ist -- ein Histogramm über das ganze Bild
 * kann das nicht zeigen, weil es den Ort wegwirft.
 */
export function parade(daten, breite, hoehe, spalten = 48, stufen = 48) {
    const r = leer(spalten * stufen);
    const g = leer(spalten * stufen);
    const b = leer(spalten * stufen);
    let groesste = 0;
    for (let y = 0; y < hoehe; y++) {
        for (let x = 0; x < breite; x++) {
            const i = (y * breite + x) * 4;
            const spalte = Math.min(spalten - 1, Math.floor((x / breite) * spalten));
            const cr = spalte * stufen + fach(daten[i] ?? 0, stufen);
            const cg = spalte * stufen + fach(daten[i + 1] ?? 0, stufen);
            const cb = spalte * stufen + fach(daten[i + 2] ?? 0, stufen);
            groesste = Math.max(groesste, (r[cr] += 1), (g[cg] += 1), (b[cb] += 1));
        }
    }
    return { spalten, stufen, r, g, b, groesste };
}
/** Rec. 709: aus R/G/B die beiden Farbachsen, jeweils -0,5 .. 0,5. */
export function farbachsen(r, g, b) {
    return {
        cb: (-0.1146 * r - 0.3854 * g + 0.5 * b) / 255,
        cr: (0.5 * r - 0.4542 * g - 0.0458 * b) / 255,
    };
}
/**
 * Das Vektorskop: wo im Farbkreis das Bild liegt, ohne Rücksicht auf die
 * Helligkeit. Die Mitte ist grau; je weiter aussen, desto satter.
 */
export function vektorskop(daten, gitter = 48) {
    const werte = leer(gitter * gitter);
    let groesste = 0;
    for (let i = 0; i + 3 < daten.length; i += 4) {
        const { cb, cr } = farbachsen(daten[i] ?? 0, daten[i + 1] ?? 0, daten[i + 2] ?? 0);
        const x = Math.min(gitter - 1, Math.max(0, Math.floor((cb + 0.5) * gitter)));
        // Oben ist mehr Rot: die Zeile zählt von oben, der Wert von unten.
        const y = Math.min(gitter - 1, Math.max(0, Math.floor((0.5 - cr) * gitter)));
        groesste = Math.max(groesste, (werte[y * gitter + x] += 1));
    }
    return { gitter, werte, groesste };
}
/**
 * Der Satz unter den Kurven: liegt etwas ausserhalb des Sendebereichs?
 *
 * Prozente und nicht „ja/nein“: ein paar Punkte über Weiss hat fast jedes
 * Bild mit einer Lampe darin, und das ist kein Fehler. Zwanzig Prozent sind
 * einer.
 */
export function sendeSatz(h) {
    const p = (x) => `${(x * 100).toFixed(1).replace('.', ',')} %`;
    if (h.unten === 0 && h.oben === 0)
        return 'Alles im Sendebereich (16–235).';
    const teile = [];
    if (h.unten > 0)
        teile.push(`${p(h.unten)} unter Schwarz`);
    if (h.oben > 0)
        teile.push(`${p(h.oben)} über Weiss`);
    return `${teile.join(', ')} — ausserhalb 16–235.`;
}
//# sourceMappingURL=messgeraete.js.map